import { useMemo } from 'react';

import { LearningProgressStatus } from 'src/services/http/progress';
import { NotificationItem } from 'src/services/http/notifications';
import {
  MyLearningResourceData,
  RecentlyViewedLessonResource,
  ArchivedContentResource,
} from 'src/pages/MyLearningPage/types';

import { CONTENT_TYPE_VALUES } from './constants';

// Collection types for Contentful data
interface LessonItem {
  duration?: number;
  [key: string]: unknown;
}

interface CourseItem {
  lessonsCollection?: {
    items: LessonItem[];
  };
  [key: string]: unknown;
}

interface LessonsCollection {
  items: LessonItem[];
}

interface CoursesCollection {
  items: CourseItem[];
}

// Type for flattened items
export type FlattenedItem = {
  contentType: 'lesson' | 'course' | 'learningPath';
  contentfulId: string;
  progressTotal: number;
  subCollectionTotal: number;
  slug?: string;
  title: string;
  product?: string;
  productsList?: string[]; // Array of products for filtering
  completedDate?: string;
  startedDate?: string;
  badgeUrl?: string;
  learningPathSlug?: string;
  learningPathContentfulId?: string | null;
  products?: string;
  status?: LearningProgressStatus;
  lessonsCollection?: LessonsCollection;
  coursesCollection?: CoursesCollection;
  unseenNotifications?: NotificationItem[];
  historicalNotifications?: NotificationItem[];
  isSaved?: boolean;
  isRetired?: boolean;
  lastVisitedDate?: Date;
  courseSlug?: string;
  duration?: number;
};

// Helper: Calculate course duration
const getCourseDuration = (course: { lessonsCollection?: LessonsCollection; [key: string]: unknown }): number => {
  return course.lessonsCollection?.items.reduce((acc: number, lesson) => acc + (lesson?.duration || 0), 0) || 0;
};

// Helper: Calculate learning path duration
const getLearningPathDuration = (learningPath: {
  coursesCollection?: CoursesCollection;
  [key: string]: unknown;
}): number => {
  return (
    learningPath.coursesCollection?.items.reduce(
      (acc: number, course) =>
        acc + (course.lessonsCollection?.items.reduce((sum: number, lesson) => sum + (lesson?.duration || 0), 0) || 0),
      0,
    ) || 0
  );
};

export const useMyLearningData = (
  recentlyViewedLessons: RecentlyViewedLessonResource[] | null,
  learningData: MyLearningResourceData | null,
  archivedContent: ArchivedContentResource[] | null,
): FlattenedItem[] => {
  return useMemo((): FlattenedItem[] => {
    return [
      // Lessons from recently viewed (duration already exists)
      ...(recentlyViewedLessons || []).map((item) => ({
        ...item,
        contentType: CONTENT_TYPE_VALUES.LESSON,
      })),
      // Courses (calculate total duration)
      ...(learningData?.courses || []).map((item) => ({
        ...item,
        contentType: CONTENT_TYPE_VALUES.COURSE,
        duration: getCourseDuration(
          item as unknown as { lessonsCollection?: LessonsCollection; [key: string]: unknown },
        ),
      })),
      // Learning Paths (calculate total duration)
      ...(learningData?.learningPaths || []).map((item) => ({
        ...item,
        contentType: CONTENT_TYPE_VALUES.LEARNING_PATH,
        duration: getLearningPathDuration(
          item as unknown as { coursesCollection?: CoursesCollection; [key: string]: unknown },
        ),
      })),
      // Retired content - default to 'course' type since we don't have type info
      ...(archivedContent || []).map((item) => ({
        contentfulId: '', // Retired content may not have contentful ID
        progressTotal: 0,
        subCollectionTotal: 0,
        title: item.entryTitle,
        completedDate: item.completionDate.toString(),
        contentType: CONTENT_TYPE_VALUES.COURSE,
        isRetired: true,
        duration: 0,
      })),
    ];
  }, [recentlyViewedLessons, learningData, archivedContent]);
};
